import { useEffect, useState, useCallback, useRef } from "react";
import { useDebouncedValue } from "../lib/useDebounce";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Eye } from "lucide-react";
import api from "../lib/api";

export const SUPPLIER_STATUSES = ["PENDING", "UNDER_REVIEW", "PROSPECT", "ACTIVE", "PREFERRED", "INACTIVE", "SUSPENDED", "REJECTED", "DO_NOT_USE"];
export const SUPPLIER_STATUS_CLS = {
  PENDING: "bg-amber-100 text-amber-800 ring-1 ring-amber-300",
  UNDER_REVIEW: "bg-cyan-100 text-cyan-700",
  PROSPECT: "bg-slate-100 text-slate-600",
  ACTIVE: "bg-green-100 text-green-700",
  PREFERRED: "bg-blue-100 text-blue-700",
  INACTIVE: "bg-slate-200 text-slate-500",
  SUSPENDED: "bg-orange-100 text-orange-700",
  REJECTED: "bg-red-100 text-red-600",
  DO_NOT_USE: "bg-red-100 text-red-600",
};
export function SupplierStatusBadge({ status }) {
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${SUPPLIER_STATUS_CLS[status] || "bg-slate-100 text-slate-600"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}
const EMPTY = {
  company_name: "", contact_name: "", email: "", phone: "", website: "", address: "", country: "", state: "",
  products: "", supplier_type: "distributor", certifications: "", moq: "", lead_time: "",
  payment_terms: "", shipping_terms: "", warranty: "", notes: "", rating: 0, status: "PROSPECT",
  geo_coverage: "", gov_capable: false, last_contacted: "",
  naics_codes: "", uei: "", cage_code: "", insurance_info: "",
};

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const dq = useDebouncedValue(q);
  const seq = useRef(0);
  const load = useCallback(() => {
    const s = ++seq.current;
    api.get("/admin/suppliers", { params: { q: dq || undefined } })
      .then((r) => { if (s === seq.current) setItems(r.data); })
      .catch(() => { if (s === seq.current) setItems([]); });
  }, [dq]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.delete(`/admin/suppliers/${id}`); toast.success("Supplier deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-suppliers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Supplier Database</h1>
        <button data-testid="add-supplier-button" onClick={() => setEditing({ ...EMPTY })} className="btn-primary !py-2.5 !px-4 text-[13px]"><Plus size={15} /> Add Supplier</button>
      </div>
      <input data-testid="supplier-search-input" className="pg-input !py-2.5 w-[280px] mt-5" placeholder="Search suppliers..." value={q} onChange={(e) => setQ(e.target.value)} />

      {editing && <SupplierForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left text-[13.5px] min-w-[860px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="px-5 py-3">Company</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Products</th>
              <th className="px-5 py-3">Type</th><th className="px-5 py-3">Rating</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400" data-testid="suppliers-empty-state">No suppliers yet. Add your first supplier.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`supplier-row-${s.id}`}>
                <td className="px-5 py-3.5">
                  <Link to={`/admin/suppliers/${s.id}`} data-testid={`supplier-link-${s.id}`}
                    className="font-[Poppins] font-semibold text-[var(--pg-accent)] hover:underline">{s.company_name}</Link>
                  <div className="text-[12px] text-slate-500">{[s.state, s.country].filter(Boolean).join(", ")}</div>
                </td>
                <td className="px-5 py-3.5"><div>{s.contact_name || "—"}</div><div className="text-[12px] text-slate-500">{s.email}</div></td>
                <td className="px-5 py-3.5 max-w-[220px] truncate text-slate-600">{s.products || "—"}</td>
                <td className="px-5 py-3.5 capitalize text-slate-600">{s.supplier_type}</td>
                <td className="px-5 py-3.5">
                  <span className="inline-flex items-center gap-1 text-amber-500">{s.rating || 0}<Star size={13} fill="currentColor" /></span>
                </td>
                <td className="px-5 py-3.5"><SupplierStatusBadge status={s.status} /></td>
                <td className="px-5 py-3.5 whitespace-nowrap text-right">
                  <Link to={`/admin/suppliers/${s.id}`} data-testid={`supplier-review-${s.id}`}
                    className={`inline-flex items-center gap-1.5 text-[12px] font-[Poppins] font-semibold px-3 py-1.5 rounded-lg mr-1 align-middle ${
                      ["PENDING", "UNDER_REVIEW"].includes(s.status)
                        ? "bg-[var(--pg-accent)] text-white hover:bg-[var(--pg-navy)]"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}>
                    <Eye size={13} /> {["PENDING", "UNDER_REVIEW"].includes(s.status) ? "Review" : "View"}
                  </Link>
                  <button data-testid={`supplier-edit-${s.id}`} onClick={() => setEditing(s)} className="text-slate-400 hover:text-[var(--pg-accent)] p-1.5" aria-label="Edit"><Pencil size={15} /></button>
                  <button data-testid={`supplier-delete-${s.id}`} onClick={() => remove(s.id)} className="text-slate-400 hover:text-red-500 p-1.5" aria-label="Delete"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SupplierForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const h = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const isEdit = Boolean(initial.id);

  const save = async () => {
    if (!f.company_name.trim()) { toast.error("Company name is required"); return; }
    const payload = { ...f, rating: parseInt(f.rating || 0, 10), categories: f.categories || [] };
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    try {
      if (isEdit) await api.patch(`/admin/suppliers/${initial.id}`, payload);
      else await api.post("/admin/suppliers", payload);
      toast.success(isEdit ? "Supplier updated" : "Supplier added");
      onSaved();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Save failed");
    }
  };

  return (
    <div className="mt-5 bg-white rounded-xl border border-[var(--pg-accent)]/40 p-6" data-testid="supplier-form">
      <h2 className="font-[Poppins] font-bold text-[17px] text-[var(--pg-navy)] mb-4">{isEdit ? "Edit Supplier" : "New Supplier"}</h2>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className="pg-label">Company Name *</label><input data-testid="supplier-name-input" className="pg-input !py-2.5" value={f.company_name} onChange={h("company_name")} /></div>
        <div><label className="pg-label">Contact Name</label><input data-testid="supplier-contact-input" className="pg-input !py-2.5" value={f.contact_name} onChange={h("contact_name")} /></div>
        <div><label className="pg-label">Email</label><input data-testid="supplier-email-input" className="pg-input !py-2.5" value={f.email} onChange={h("email")} /></div>
        <div><label className="pg-label">Phone</label><input data-testid="supplier-phone-input" className="pg-input !py-2.5" value={f.phone} onChange={h("phone")} /></div>
        <div><label className="pg-label">Website</label><input data-testid="supplier-website-input" className="pg-input !py-2.5" value={f.website} onChange={h("website")} /></div>
        <div><label className="pg-label">Address</label><input data-testid="supplier-address-input" className="pg-input !py-2.5" value={f.address} onChange={h("address")} /></div>
        <div><label className="pg-label">Country</label><input data-testid="supplier-country-input" className="pg-input !py-2.5" value={f.country} onChange={h("country")} /></div>
        <div><label className="pg-label">State</label><input data-testid="supplier-state-input" className="pg-input !py-2.5" value={f.state} onChange={h("state")} /></div>
        <div>
          <label className="pg-label">Type</label>
          <select data-testid="supplier-type-select" className="pg-input !py-2.5" value={f.supplier_type} onChange={h("supplier_type")}>
            <option value="manufacturer">Manufacturer</option><option value="distributor">Distributor</option>
          </select>
        </div>
        <div className="sm:col-span-3"><label className="pg-label">Products / Categories</label><input data-testid="supplier-products-input" className="pg-input !py-2.5" value={f.products} onChange={h("products")} placeholder="e.g. valves, pumps, PPE, IT hardware..." /></div>
        <div><label className="pg-label">Certifications</label><input data-testid="supplier-certs-input" className="pg-input !py-2.5" value={f.certifications} onChange={h("certifications")} /></div>
        <div><label className="pg-label">NAICS Codes</label><input data-testid="supplier-naics-input" placeholder="e.g. 423840, 339112" className="pg-input !py-2.5" value={f.naics_codes || ""} onChange={h("naics_codes")} /></div>
        <div><label className="pg-label">UEI</label><input data-testid="supplier-uei-input" className="pg-input !py-2.5" value={f.uei || ""} onChange={h("uei")} /></div>
        <div><label className="pg-label">CAGE Code</label><input data-testid="supplier-cage-input" className="pg-input !py-2.5" value={f.cage_code || ""} onChange={h("cage_code")} /></div>
        <div className="sm:col-span-2"><label className="pg-label">Insurance Information</label><input data-testid="supplier-insurance-input" placeholder="e.g. GL $1M/$2M, carrier, expiry" className="pg-input !py-2.5" value={f.insurance_info || ""} onChange={h("insurance_info")} /></div>
        <div><label className="pg-label">MOQ</label><input data-testid="supplier-moq-input" className="pg-input !py-2.5" value={f.moq} onChange={h("moq")} /></div>
        <div><label className="pg-label">Lead Time</label><input data-testid="supplier-lead-time-input" className="pg-input !py-2.5" value={f.lead_time} onChange={h("lead_time")} /></div>
        <div><label className="pg-label">Payment Terms</label><input data-testid="supplier-payment-terms-input" className="pg-input !py-2.5" value={f.payment_terms} onChange={h("payment_terms")} /></div>
        <div><label className="pg-label">Shipping Terms</label><input data-testid="supplier-shipping-terms-input" className="pg-input !py-2.5" value={f.shipping_terms} onChange={h("shipping_terms")} /></div>
        <div><label className="pg-label">Warranty</label><input data-testid="supplier-warranty-input" className="pg-input !py-2.5" value={f.warranty} onChange={h("warranty")} /></div>
        <div><label className="pg-label">Geographic Coverage</label><input data-testid="supplier-geo-input" placeholder="e.g. US nationwide, TX/LA, global" className="pg-input !py-2.5" value={f.geo_coverage} onChange={h("geo_coverage")} /></div>
        <div><label className="pg-label">Last Contacted</label><input data-testid="supplier-last-contacted-input" type="date" className="pg-input !py-2.5" value={f.last_contacted} onChange={h("last_contacted")} /></div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-[13.5px] font-medium text-[var(--pg-navy)] cursor-pointer">
            <input data-testid="supplier-gov-capable-input" type="checkbox" className="w-4 h-4 accent-[var(--pg-accent)]" checked={!!f.gov_capable}
              onChange={(e) => setF((x) => ({ ...x, gov_capable: e.target.checked }))} />
            Government contracting capable
          </label>
        </div>
        <div>
          <label className="pg-label">Rating (0-5)</label>
          <select data-testid="supplier-rating-select" className="pg-input !py-2.5" value={f.rating} onChange={h("rating")}>
            {[0, 1, 2, 3, 4, 5].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="pg-label">Status</label>
          <select data-testid="supplier-status-select" className="pg-input !py-2.5" value={f.status} onChange={h("status")}>
            {SUPPLIER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div className="sm:col-span-3"><label className="pg-label">Notes</label><textarea data-testid="supplier-notes-input" rows={2} className="pg-input !py-2.5" value={f.notes} onChange={h("notes")} /></div>
      </div>
      <div className="mt-4 flex gap-3">
        <button data-testid="supplier-save-button" onClick={save} className="btn-primary !py-2.5 !px-5 text-[13px]">{isEdit ? "Save Changes" : "Add Supplier"}</button>
        <button data-testid="supplier-cancel-button" onClick={onClose} className="text-[13px] font-[Poppins] font-semibold text-slate-500 hover:text-[var(--pg-navy)] px-4">Cancel</button>
      </div>
    </div>
  );
}
